import { handleServiceError, createServiceResponse } from '@/helpers/errorHandling';
import { t } from '@/i18n';
import AppMessages from '@/models/core/AppMessages';
import { supabase } from '@/supabase';
import type { ServiceResponse } from '@/types/core';
import type { EmployeeResponse } from '@/types/employee';
import type { EmployeeWithRoleResponse } from '@/types/employee';
import type { EmployeeViewResponse, EmployeeViewFilters } from '@/types/employee';
import { createUser } from '@/services/createUser';
import ServiceDomains from '@/constants/ServiceDomains';

/**
 * Gets all employees
 * @returns ServiceResponse with employees data or error
 */
export async function getAll(): Promise<ServiceResponse<EmployeeResponse[]>> {
  try {
    const { data, error: err } = await supabase.from('employees').select('*').order('first_name', { ascending: true });

    if (err) {
      return createServiceResponse(null, err, ServiceDomains.Employee);
    }

    return { data: data as EmployeeResponse[], error: null };
  } catch (err: unknown) {
    return createServiceResponse(null, err, ServiceDomains.Employee);
  }
}

/**
 * Gets an employee by UID
 * @param uid Employee UID
 * @returns ServiceResponse with employee data or error
 */
export async function getByUid(uid: string): Promise<ServiceResponse<EmployeeResponse>> {
  try {
    const { data, error: err } = await supabase.from('employees').select('*').eq('uid', uid).single();

    if (err) {
      return createServiceResponse(null, err, ServiceDomains.Employee);
    }

    return { data: data as EmployeeResponse, error: null };
  } catch (err: unknown) {
    return createServiceResponse(null, err, ServiceDomains.Employee);
  }
}

/**
 * Creates a new employee
 * @param employee Employee data to create
 * @returns ServiceResponse with created employee UID or error
 */
export async function create(employee: {
  first_name: string;
  last_name: string;
  email?: string;
  pin?: string;
  role_uid: string;
}): Promise<ServiceResponse<string>> {
  try {
    // Validate required role
    if (!employee.role_uid) {
      const error = handleServiceError('ROLE_REQUIRED', ServiceDomains.Employee);
      return { data: null, error };
    }

    // If PIN is provided, check if it already exists
    if (employee.pin) {
      const { data: pinExists, error: pinError } = await checkPinExists(employee.pin);

      if (pinError) {
        return { data: null, error: pinError };
      }

      if (pinExists) {
        const error = handleServiceError('PIN_ALREADY_EXISTS', ServiceDomains.Employee);
        return { data: null, error };
      }
    }

    let userData = null;

    // If email is provided, check if it already exists and create user
    if (employee.email) {
      const { data: existingEmployee } = await supabase.from('employees').select('uid').eq('email', employee.email).single();

      if (existingEmployee) {
        const error = handleServiceError('EMAIL_ALREADY_EXISTS', ServiceDomains.Employee);
        return { data: null, error };
      }

      // Create or get user via edge function
      const { data: userResult, error: userError } = await createUser(employee.email);

      if (userError || !userResult) {
        return { data: null, error: userError || 'Failed to create user' };
      }

      userData = userResult;
    }

    // Create employee with or without user_uid
    const { data, error: err } = await supabase
      .from('employees')
      .insert({
        email: employee.email || null,
        first_name: employee.first_name,
        last_name: employee.last_name,
        pin: employee.pin || null,
        role_uid: employee.role_uid,
        user_uid: userData?.uid || null,
      })
      .select('uid')
      .single();

    if (err) {
      return createServiceResponse(null, err, ServiceDomains.Employee);
    }

    const successMessage = t('employees.createSuccess');
    AppMessages.success(successMessage);
    return { data: data.uid, error: null };
  } catch (err: unknown) {
    return createServiceResponse(null, err, ServiceDomains.Employee);
  }
}

/**
 * Updates an existing employee
 * @param uid Employee UID
 * @param employee Employee data to update
 * @returns ServiceResponse with success or error
 */
export async function update(
  uid: string,
  employee: {
    first_name: string;
    last_name: string;
    email?: string;
    pin?: string;
    role_uid?: string;
  },
): Promise<ServiceResponse<boolean>> {
  try {
    // If PIN is provided, check if it already exists for other employees
    if (employee.pin) {
      const { data: pinExists, error: pinError } = await checkPinExists(employee.pin);

      if (pinError) {
        return { data: null, error: pinError };
      }

      if (pinExists) {
        // Check if the PIN belongs to the current employee being updated
        const { data: currentEmployee } = await getByUid(uid);
        if (currentEmployee && currentEmployee.pin !== employee.pin) {
          const error = handleServiceError('PIN_ALREADY_EXISTS', ServiceDomains.Employee);
          return { data: null, error };
        }
      }
    }

    const { error: err } = await supabase.from('employees').update(employee).eq('uid', uid);

    if (err) {
      return createServiceResponse(null, err, ServiceDomains.Employee);
    }

    const successMessage = t('employees.updateSuccess');
    AppMessages.success(successMessage);
    return { data: true, error: null };
  } catch (err: unknown) {
    return createServiceResponse(null, err, ServiceDomains.Employee);
  }
}

/**
 * Gets all employee settings with filters, search, sorting and pagination
 * @param filters Filter parameters including search, sort and pagination
 * @returns ServiceResponse with employees data and total count or error
 */
export async function getAllWithFilters(filters: EmployeeViewFilters & { pageSize: number; includeDeleted?: boolean }): Promise<
  ServiceResponse<{
    employees: EmployeeViewResponse[];
    total: number;
  }>
> {
  try {
    // Use the employee_list_view for complete data
    let query = supabase.from('employee_list_view').select('*', { count: 'exact' });

    // Apply deleted filter
    if (!filters.includeDeleted) {
      query = query.is('deleted_at', null);
    }

    // Apply text filter (search in full_name and email)
    if (filters.textFilter) {
      query = query.or(`full_name.ilike.%${filters.textFilter}%,email.ilike.%${filters.textFilter}%`);
    }

    // Apply role filter
    if (filters.roleFilter && filters.roleFilter.trim() !== '') {
      query = query.eq('role_name', filters.roleFilter);
    }

    // Apply sorting
    const ascending = filters.sortOrder === 'asc';
    query = query.order(filters.sortBy, { ascending });

    // Apply pagination
    const from = (filters.pageNumber - 1) * filters.pageSize;
    const to = from + filters.pageSize - 1;
    query = query.range(from, to);

    const { data, error: err, count } = await query;

    if (err) {
      return createServiceResponse(null, err, ServiceDomains.Employee);
    }

    // Transform data to match EmployeeViewResponse format
    const transformedData: EmployeeViewResponse[] = (data || []).map(
      (employee: {
        uid: string;
        full_name: string;
        email: string | null;
        pin: string | null;
        last_activity: string | null;
        role_name: string | null;
        deleted_at: string | null;
        is_inactive: boolean;
        created_at: string;
        updated_at: string;
      }) => {
        return {
          created_at: employee.created_at,
          deleted_at: employee.deleted_at,
          email: employee.email,
          full_name: employee.full_name,
          is_inactive: employee.is_inactive,
          last_activity: employee.last_activity,
          pin: employee.pin,
          role_name: employee.role_name,
          uid: employee.uid,
          updated_at: employee.updated_at,
        };
      },
    );

    return {
      data: {
        employees: transformedData,
        total: count || 0,
      },
      error: null,
    };
  } catch (err: unknown) {
    return createServiceResponse(null, err, ServiceDomains.Employee);
  }
}

/**
 * Checks if a PIN code already exists
 * @param pinCode 6-digit PIN code
 * @returns ServiceResponse with boolean result or error
 */
export async function checkPinExists(pinCode: string): Promise<ServiceResponse<boolean>> {
  try {
    const { data, error: err } = await supabase.rpc('check_pin_exists', {
      pin_code: pinCode,
    });

    if (err) {
      return createServiceResponse(null, err, ServiceDomains.Employee);
    }

    return { data: data as boolean, error: null };
  } catch (err: unknown) {
    return createServiceResponse(null, err, ServiceDomains.Employee);
  }
}

/**
 * Gets an employee by PIN code
 * @param pinCode 6-digit PIN code
 * @returns AuthResponse with employee data or error
 */
export async function getEmployeeByPin(pinCode: string): Promise<ServiceResponse<EmployeeWithRoleResponse[]>> {
  try {
    const { data, error: err } = await supabase.rpc('get_employee_by_pin', {
      pin_code: pinCode,
    });

    if (err) {
      return createServiceResponse(null, err, ServiceDomains.Employee);
    }

    if (!data) {
      const error = handleServiceError('EMPLOYEE_NOT_FOUND_WITH_PIN', ServiceDomains.Employee);
      return { data: null, error };
    }

    return { data: data as EmployeeWithRoleResponse[], error: null };
  } catch (err: unknown) {
    return createServiceResponse(null, err, ServiceDomains.Employee);
  }
}

/**
 * Gets an employee by user UID with role information
 * @param userUid User UID from auth.users
 * @returns ServiceResponse with employee and role data or error
 */
export async function getByUserUidWithRole(userUid: string): Promise<ServiceResponse<EmployeeWithRoleResponse>> {
  try {
    const { data, error: err } = await supabase
      .from('employees')
      .select(
        `
        *,
        roles!inner(name, slug)
      `,
      )
      .eq('user_uid', userUid)
      .is('deleted_at', null)
      .single();

    if (err) {
      return createServiceResponse(null, err, ServiceDomains.Employee);
    }

    // Transform the nested role data to flat structure
    const transformedData: EmployeeWithRoleResponse = {
      ...data,
      role_name: data.roles?.name || null,
      role_slug: data.roles?.slug || null,
    };

    // Remove the nested roles object
    delete (transformedData as EmployeeWithRoleResponse & { roles?: unknown }).roles;

    return { data: transformedData, error: null };
  } catch (err: unknown) {
    return createServiceResponse(null, err, ServiceDomains.Employee);
  }
}

/**
 * Soft deletes an employee by setting deleted_at timestamp
 * @param uid Employee UID
 * @returns ServiceResponse with success or error
 */
export async function softDelete(uid: string): Promise<ServiceResponse<string>> {
  try {
    const { data, error: err } = await supabase.rpc('delete_employee', { p_employee_uid: uid });

    if (err) {
      return createServiceResponse(null, err, ServiceDomains.Employee);
    }

    const successMessage = t('employees.deleteSuccess');
    AppMessages.success(successMessage);
    return { data, error: null };
  } catch (err: unknown) {
    return createServiceResponse(null, err, ServiceDomains.Employee);
  }
}

/**
 * Restores a soft deleted employee by setting deleted_at to null
 * @param uid Employee UID
 * @returns ServiceResponse with success or error
 */
export async function restore(uid: string): Promise<ServiceResponse<string>> {
  try {
    // First validate the employee can be restored using the database function
    const { error: validateError } = await supabase.rpc('restore_employee', { p_employee_uid: uid });

    if (validateError) {
      return createServiceResponse(null, validateError, ServiceDomains.Employee);
    }

    // Get the employee data
    const { data: employee, error: getError } = await getByUid(uid);
    if (getError || !employee) {
      return { data: null, error: getError || 'Employee not found' };
    }

    let userData = null;

    // If employee has an email, create or get user via edge function
    if (employee.email) {
      const { data: userResult, error: userError } = await createUser(employee.email);

      if (userError || !userResult) {
        return { data: null, error: userError || 'Failed to create user' };
      }

      userData = userResult;
    }

    // Restore the employee with or without user_uid
    const { error: err } = await supabase
      .from('employees')
      .update({
        deleted_at: null,
        user_uid: userData?.uid || null,
      })
      .eq('uid', uid);

    if (err) {
      return createServiceResponse(null, err, ServiceDomains.Employee);
    }

    const successMessage = t('employees.restoreSuccess');
    AppMessages.success(successMessage);
    return { data: uid, error: null };
  } catch (err: unknown) {
    return createServiceResponse(null, err, ServiceDomains.Employee);
  }
}
